import { redirect, RouteObject } from "react-router-dom"

import { ErrorPage } from "~/src/components/ErrorPage"
import {
  QuickSendConfirmationContent,
  QuickSendConfirmationContentProps,
} from "~/src/components/QuickSends/QuickSendConfirmationContent"
import {
  QuickSendEditDetailsContent,
  QuickSendEditDetailsContentProps,
} from "~/src/components/QuickSends/QuickSendEditDetailsContent"
import {
  QuickSendEditSelectionsContent,
  QuickSendEditSelectionsContentProps,
} from "~/src/components/QuickSends/QuickSendEditSelectionsContent"
import {
  QuickSendEditStoreContent,
  QuickSendEditStoreContentProps,
} from "~/src/components/QuickSends/QuickSendEditStoreContent"
import { QuickSendReviewContent, QuickSendReviewContentProps } from "~/src/components/QuickSends/QuickSendReviewContent"
import { appClient, AppClientError } from "~/src/lib/appClients"
import { summarizeMergeFields } from "~/src/lib/quickSend"

const createRoutes = (): RouteObject[] => [
  {
    path: "/quick_sends/:id",
    hydrateFallbackElement: <></>,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "store",
        element: <QuickSendEditStoreContent />,
        async action({ request, params }) {
          const { id } = params
          const formData = await request.formData()

          try {
            const response = await appClient.patch(`quick_sends/${id}/store`, formData, {
              headers: { Accept: "application/json" },
            })

            return redirect(response.data?.nextUrl)
          } catch (error) {
            if (error instanceof AppClientError && typeof error?.status === "number" && error.status < 500) {
              return error.response?.data
            }

            throw error
          }
        },
        async loader({ params }): Promise<QuickSendEditStoreContentProps> {
          const { id } = params

          return appClient
            .get(`quick_sends/${id}/store.json`, {
              headers: { Accept: "application/json" },
            })
            .then(({ data }) => data)
        },
      },
      {
        path: "selections",
        element: <QuickSendEditSelectionsContent />,
        async loader({ request, params }): Promise<{ data: Promise<QuickSendEditSelectionsContentProps> }> {
          const { id } = params
          const url = new URL(request.url)

          const data = appClient
            .get(`quick_sends/${id}/selections.json`, {
              headers: { Accept: "application/json" },
              params: url.searchParams,
            })
            .then(({ data }) => data)

          // Not awaiting the XHR promise, and returning it in an object allows us to use <Suspense> in the component
          // and <Await> to do something with the UI while the data is loading instead of giving the impression
          // that the page is frozen.
          return { data }
        },
        async action({ request, params }) {
          const { id } = params
          const formData = await request.formData()
          try {
            const response = await appClient.patch(`quick_sends/${id}/selections`, formData, {
              headers: { Accept: "application/json" },
            })
            return redirect(response.data?.nextUrl)
          } catch (error) {
            if (error instanceof AppClientError && typeof error?.status === "number" && error.status < 500) {
              return error.response?.data
            }

            throw error
          }
        },
        children: [
          {
            path: ":collectionable_id/modal",
            async loader({ params }) {
              const { id, collectionable_id } = params
              const { data } = await appClient.get(`quick_sends/${id}/selections/${collectionable_id}/modal.json`, {
                headers: { Accept: "application/json" },
              })

              return data
            },
          },
        ],
      },
      {
        path: "details",
        element: <QuickSendEditDetailsContent />,
        shouldRevalidate() {
          return false
        },
        async action({ request, params }) {
          const { id } = params
          const formData = await request.formData()

          const confirmed = verifyTemplateMergeFields(formData)

          if (confirmed === false) return {}

          try {
            const response = await appClient.patch(`quick_sends/${id}/details`, formData, {
              headers: { Accept: "application/json" },
            })
            return redirect(response.data?.nextUrl)
          } catch (error) {
            if (error instanceof AppClientError && typeof error?.status === "number" && error.status < 500) {
              return error.response?.data
            }

            throw error
          }
        },
        async loader({ params }): Promise<QuickSendEditDetailsContentProps> {
          const { id } = params

          const response = await appClient.get(`quick_sends/${id}/details.json`, {
            headers: { Accept: "application/json" },
          })

          return response.data
        },
      },
      {
        path: "review",
        element: <QuickSendReviewContent />,
        async loader({ params }): Promise<QuickSendReviewContentProps> {
          const { id } = params

          const response = await appClient.get(`quick_sends/${id}/review.json`, {
            headers: { Accept: "application/json" },
          })

          return response.data
        },
      },
      {
        path: "confirm",
        element: <QuickSendConfirmationContent />,
        async loader({ params }): Promise<QuickSendConfirmationContentProps> {
          const { id } = params

          const response = await appClient.get(`quick_sends/${id}/confirm.json`, {
            headers: { Accept: "application/json" },
          })

          return response.data
        },
      },
    ],
  },
]

function verifyTemplateMergeFields(formData: FormData): boolean {
  let warningMessage = ""

  const notecardMessage = formData.get("quick_send[notecard_message]") as string
  const emailBody = formData.get("quick_send[email_body]") as string
  const usingEmail = formData.get("quick_send[using]") === "email"

  let [, allUnsupportedMergeFields] = summarizeMergeFields(notecardMessage)

  if (usingEmail) {
    const [validEmailMergeFields, invalidEmailMergeFields] = summarizeMergeFields(emailBody)
    const emailMissingRecipientName = !validEmailMergeFields.includes("recipient_name")

    if (emailMissingRecipientName) {
      warningMessage +=
        "Couldn’t find {{ recipient_name }} in the email body. Are you sure you want to send recipient emails without their name?\n\n"
    }

    allUnsupportedMergeFields = allUnsupportedMergeFields.concat(invalidEmailMergeFields)
  }

  if (allUnsupportedMergeFields.length > 0) {
    warningMessage += `We found the following unsupported fields: ${allUnsupportedMergeFields.map((field) => `{{ ${field} }}`).join(", ")}.\n\n`
  }

  return warningMessage.length > 0 ? confirm(`${warningMessage}Would you still like to continue?`) : true
}

export default createRoutes
